import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/layout/PageHeader";
import ProgressRing from "../components/shared/ProgressRing";
import { SURAHS } from "../services/quranService";
import { toast } from "sonner";
import {
  Plus,
  BookOpen,
  Brain,
  CheckCircle2,
  Clock,
  Trash2,
  RotateCcw,
  Loader2,
  Calendar,
  TrendingUp,
  Award,
  Check,
  ToggleRight,
  ToggleLeft,
  Lightbulb,
  Building,
  Moon,
  Sun,
  Star,
  Heart,
  Sunset,
  Sunrise,
  Repeat,
  Bell,
  BarChart3,
  Hand,
  PlayCircle,
  Coins,
  RefreshCw,
  ChevronLeft,
  Flame
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";

const Habits = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [habitsData, setHabitsData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [activeTab, setActiveTab] = useState("today");
  const [initializing, setInitializing] = useState(false);
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [newHabit, setNewHabit] = useState({
    name: "",
    description: "",
    category: "general",
    icon: "check-circle",
    color: "#6B7280",
    target_per_day: 1,
  });
  const hasInitializedRef = useRef(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmAgree, setConfirmAgree] = useState(false);
  const [detailHabit, setDetailHabit] = useState(null);
  const [detailSeries, setDetailSeries] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBottomTicks, setDetailBottomTicks] = useState([]);
  const [detailTopTicks, setDetailTopTicks] = useState([]);
  const [detailShowAllDates, setDetailShowAllDates] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const habitsLoadRef = useRef({ inFlight: false, lastAt: 0 });
  const HABITS_CACHE_KEY = "quran-habits-cache";
  const HABITS_PROGRESS_CACHE_KEY = "quran-habits-progress-cache";

  const iconMap = {
    "book-open": BookOpen,
    "brain": Brain,
    "refresh-cw": RefreshCw,
    "lightbulb": Lightbulb,
    "mosque": Building,
    "moon": Moon,
    "sun": Sun,
    "star": Star,
    "heart": Heart,
    "clock": Clock,
    "sunset": Sunset,
    "sunrise": Sunrise,
    "repeat": Repeat,
    "hands": Hand,
    "pray": Hand,
    "play-circle": PlayCircle,
    "hand-coins": Coins,
  };

  const categoryColors = {
    quran: "#0D9488",
    shalat: "#7C3AED",
    infaq: "#F59E0B",
    intentions: "#22C55E",
    general: "#6B7280",
  };

  const defaultHabits = [
    "Tilawah 1 juz",
    "Hafalan 1 ayat",
    "Murajaah",
    "Tadabbur",
    "Shalat Subuh",
    "Shalat Zuhur",
    "Shalat Ashar",
    "Shalat Maghrib",
    "Shalat Isya",
    "Qabliyah Subuh",
    "Qabliyah Dzuhur",
    "Ba'diyah Dzuhur",
    "Qabliyah Ashar",
    "Ba'diyah Maghrib",
    "Ba'diyah Isya",
    "Tahajud",
    "Witir",
    "Taubat",
    "Dhuha",
    "Hajat",
    "Syuruk",
    "Infaq hari ini",
    "Niat & doa",
    "Shalawat",
    "Istighfar",
    "Puasa",
  ];
  const defaultOrderMap = defaultHabits.reduce((acc, name, index) => {
    acc[name.toLowerCase()] = index + 1;
    return acc;
  }, {});

  // Helper function to get local date in YYYY-MM-DD format
  const getLocalDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const sortHabits = (habits) => habits.slice().sort((a, b) => {
    const aSystem = a.is_system_default ? 0 : 1;
    const bSystem = b.is_system_default ? 0 : 1;
    if (aSystem !== bSystem) {
      return aSystem - bSystem;
    }
    const aOrder = a.order ?? 9999;
    const bOrder = b.order ?? 9999;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return (a.name || "").localeCompare(b.name || "");
  });

  const loadData = useCallback(async ({ force = false } = {}) => {
    try {
      const now = Date.now();
      if (!force) {
        if (habitsLoadRef.current.inFlight) return;
        if (now - habitsLoadRef.current.lastAt < 5000) {
          return;
        }
        try {
          const cachedPayload = JSON.parse(localStorage.getItem(HABITS_CACHE_KEY) || "null");
          const cachedProgress = JSON.parse(localStorage.getItem(HABITS_PROGRESS_CACHE_KEY) || "null");
          if (cachedPayload?.data && now - (cachedPayload.fetchedAt || 0) < 60000) {
            setHabitsData(cachedPayload.data);
            if (cachedProgress?.data && now - (cachedProgress.fetchedAt || 0) < 60000) {
              setProgressData(cachedProgress.data);
            }
            setLoading(false);
            return;
          }
        } catch {}
      }
      habitsLoadRef.current.inFlight = true;
      habitsLoadRef.current.lastAt = now;
      setLoading(true);
      const habitsRes = await apiClient.get("/habits");
      setLastRefresh(new Date());
      const hasSystemDefaults = habitsRes.data?.some(h => h.is_system_default === true);
      const normalize = (value) => (value || "").trim().toLowerCase();
      const systemNames = (habitsRes.data || [])
        .filter((habit) => habit.is_system_default)
        .map((habit) => normalize(habit.name));
      const isMissingDefaults = defaultHabits.some((name) => !systemNames.includes(normalize(name)));
      const orderMismatch = (habitsRes.data || []).some((habit) => {
        if (!habit.is_system_default) {
          return false;
        }
        const expectedOrder = defaultOrderMap[normalize(habit.name)];
        return expectedOrder && habit.order !== expectedOrder;
      });
      const shouldInitialize = !hasSystemDefaults || isMissingDefaults || orderMismatch;

      if (shouldInitialize && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        setInitializing(true);
        await apiClient.post("/habits/initialize");
        setInitializing(false);
        const refreshedHabitsRes = await Promise.race([
          apiClient.get("/habits"),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
        ]);
        const todayRes = await Promise.race([
          apiClient.get(`/habits/today?date=${getLocalDate()}`),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
        ]);
        const completedIds = new Set();
        Object.values(todayRes.data?.categories || {}).forEach((category) => {
          category.habits.forEach((habit) => {
            if (habit.completed) {
              completedIds.add(habit.habit_id);
            }
          });
        });
        const categories = {
          quran: { name: "Al-Qur'an", habits: [] },
          shalat: { name: isEnglish ? "Prayer" : "Shalat", habits: [] },
          infaq: { name: "Infaq", habits: [] },
          intentions: { name: isEnglish ? "Intentions" : "Niat & Doa", habits: [] },
          general: { name: isEnglish ? "Other" : "Lainnya", habits: [] },
        };
        const allHabits = sortHabits(refreshedHabitsRes.data || []);
        allHabits.forEach((habit) => {
          const categoryKey = categories[habit.category] ? habit.category : "general";
          categories[categoryKey].habits.push({
            ...habit,
            completed: completedIds.has(habit.habit_id),
          });
        });
        const payload = {
          ...todayRes.data,
          categories,
          total_completed: todayRes.data?.total_completed || completedIds.size,
          total_habits: todayRes.data?.total_habits || allHabits.filter((habit) => habit.enabled).length,
        };
        setHabitsData(payload);
        try {
          localStorage.setItem(HABITS_CACHE_KEY, JSON.stringify({ data: payload, fetchedAt: Date.now() }));
        } catch {}
        setLoading(false);
        return;
      }

      const todayRes = await Promise.race([
        apiClient.get(`/habits/today?date=${getLocalDate()}`),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
      ]);
      const completedIds = new Set();
      Object.values(todayRes.data?.categories || {}).forEach((category) => {
        category.habits.forEach((habit) => {
          if (habit.completed) {
            completedIds.add(habit.habit_id);
          }
        });
      });
      const categories = {
        quran: { name: "Al-Qur'an", habits: [] },
        shalat: { name: isEnglish ? "Prayer" : "Shalat", habits: [] },
        infaq: { name: "Infaq", habits: [] },
        intentions: { name: isEnglish ? "Intentions" : "Niat & Doa", habits: [] },
        general: { name: isEnglish ? "Other" : "Lainnya", habits: [] },
      };
      const allHabits = sortHabits(habitsRes.data || []);
      allHabits.forEach((habit) => {
        const categoryKey = categories[habit.category] ? habit.category : "general";
        categories[categoryKey].habits.push({
          ...habit,
          completed: completedIds.has(habit.habit_id),
        });
      });
      const payload = {
        ...todayRes.data,
        categories,
        total_completed: todayRes.data?.total_completed || completedIds.size,
        total_habits: todayRes.data?.total_habits || allHabits.filter((habit) => habit.enabled).length,
      };
      setHabitsData(payload);
      try {
        localStorage.setItem(HABITS_CACHE_KEY, JSON.stringify({ data: payload, fetchedAt: Date.now() }));
      } catch {}

      if (activeTab !== "today") {
        const rangeReady = dateRange.start && dateRange.end;
        const progressRes = await Promise.race([
          rangeReady
            ? apiClient.get(`/habits/progress?start_date=${dateRange.start}&end_date=${dateRange.end}`)
            : apiClient.get(`/habits/progress?period=${activeTab}`),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
        ]);
        setProgressData(progressRes.data);
        try {
          localStorage.setItem(HABITS_PROGRESS_CACHE_KEY, JSON.stringify({ data: progressRes.data, fetchedAt: Date.now() }));
        } catch {}
      }
    } catch (error) {
      console.error("Error loading habits:", error);
      toast.error(isEnglish ? "Failed to load habits" : "Gagal memuat habits");
    } finally {
      habitsLoadRef.current.inFlight = false;
      setLoading(false);
    }
  }, [user, activeTab, dateRange.start, dateRange.end]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, activeTab, loadData]);

  // Auto-refresh when tab becomes visible (handles day change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        loadData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, loadData]);

  useEffect(() => {
    if (!loading) return;
    const timeoutId = setTimeout(() => setLoading(false), 15000);
    return () => clearTimeout(timeoutId);
  }, [loading]);

  const addCustomHabit = async () => {
    if (!newHabit.name.trim()) {
      toast.error(isEnglish ? "Habit name is required" : "Nama kebiasaan harus diisi");
      return;
    }

    try {
      const res = await apiClient.post("/habits", newHabit);
      toast.success(isEnglish ? "New habit added!" : "Kebiasaan baru ditambahkan!");
      setShowAddHabit(false);
      setNewHabit({
        name: "",
        description: "",
        category: "general",
        icon: "check-circle",
        color: "#6B7280",
        target_per_day: 1,
      });
      loadData({ force: true });
    } catch (error) {
      console.error("Error adding habit:", error);
      toast.error(isEnglish ? "Failed to add habit" : "Gagal menambahkan kebiasaan");
    }
  };

  const deleteHabit = async (habitId) => {
    if (!confirm(isEnglish ? "Are you sure you want to delete this habit?" : "Apakah Anda yakin ingin menghapus kebiasaan ini?")) return;

    try {
      await apiClient.delete(`/habits/${habitId}`);
      toast.success(isEnglish ? "Habit deleted" : "Kebiasaan dihapus");
      loadData({ force: true });
    } catch (error) {
      console.error("Error deleting habit:", error);
      toast.error(isEnglish ? "Failed to delete habit" : "Gagal menghapus kebiasaan");
    }
  };

  const toggleHabit = async (habitId, completed) => {
    try {
      const todayLocal = getLocalDate();

      if (completed) {
        await apiClient.delete(`/habits/logs/${todayLocal}/${habitId}`);
      } else {
        // Send date in request body to ensure consistency
        await apiClient.post(`/habits/${habitId}/log`, { date: todayLocal });
      }

      // Request today's data with local date parameter
      await loadData({ force: true });
    } catch (error) {
      console.error("Error toggling habit:", error);
      toast.error(isEnglish ? "Failed to update habit status" : "Gagal mengubah status habit");
    }
  };

  const toggleHabitEnabled = async (habitId, enabled) => {
    try {
      await apiClient.patch(`/habits/${habitId}`, { enabled: !enabled });
      toast.success(enabled ? (isEnglish ? "Disabled" : "Nonaktifkan") : (isEnglish ? "Enabled" : "Aktifkan"));
      loadData({ force: true });
    } catch (error) {
      console.error("Error toggling habit enabled:", error);
      toast.error(isEnglish ? "Failed to update habit status" : "Gagal mengubah status habit");
    }
  };

  const getTodayCategoryChartData = () => {
    if (!habitsData?.categories) return [];
    const entries = Object.entries(habitsData.categories);
    return entries.map(([key, category]) => {
      const completedCount = category.habits.filter(h => h.completed).length;
      return {
        name: category.name,
        value: completedCount,
        key,
        color: categoryColors[key] || "#6B7280",
      };
    }).filter(d => d.value > 0);
  };

  const getProgressCategoryChartData = () => {
    if (!progressData?.habits) return [];
    const agg = {};
    progressData.habits.forEach(h => {
      const key = h.category || "general";
      agg[key] = (agg[key] || 0) + (h.total_completed || 0);
    });
    return Object.entries(agg).map(([key, value]) => ({
      name: key === "quran" ? "Al-Qur'an" : key === "shalat" ? (isEnglish ? "Prayer" : "Shalat") : key === "infaq" ? "Infaq" : key === "intentions" ? (isEnglish ? "Intentions" : "Niat & Doa") : (isEnglish ? "Other" : "Lainnya"),
      value,
      key,
      color: categoryColors[key] || "#6B7280",
    })).filter(d => d.value > 0);
  };

  const renderPercentageLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 10, fontWeight: 600 }}>
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  };

  useEffect(() => {
    const fetchDetailSeries = async () => {
      if (!detailHabit) return;
      try {
        setDetailLoading(true);
        const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 3600 * 1000);
        const start = progressData?.start_date || getLocalDate(thirtyDaysAgo);
        const end = progressData?.end_date || getLocalDate();
        // Try range endpoint first
        try {
          const res = await apiClient.get(`/habits/habit-series`, { params: { habit_id: detailHabit.habit_id, start_date: start, end_date: end } });
          const series = (res.data || []).map(item => ({ date: item.date, value: item.done ? 1 : 0 }));
          const normalized = Array.from(new Map(series.map((item) => [item.date, item.value])).entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, value]) => ({ date, value }));
          setDetailSeries(normalized);
        } catch (e) {
          // Fallback: build by per-day logs
          const dates = [];
          const startDate = new Date(start);
          const endDate = new Date(end);
          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            dates.push(new Date(d));
          }
          const requests = dates.map(d => {
            const dateStr = getLocalDate(d);
            return apiClient.get(`/habits/logs/${dateStr}`).then(r => ({ date: dateStr, logs: r.data || [] })).catch(() => ({ date: dateStr, logs: [] }));
          });
          const results = await Promise.all(requests);
          const series = results.map(r => ({
            date: r.date,
            value: (r.logs || []).some(l => l.habit_id === detailHabit.habit_id) ? 1 : 0,
          }));
          const normalized = Array.from(new Map(series.map((item) => [item.date, item.value])).entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, value]) => ({ date, value }));
          setDetailSeries(normalized);
        }
      } catch (error) {
        console.error("Error loading habit series:", error);
        toast.error(isEnglish ? "Failed to load habit chart" : "Gagal memuat grafik kebiasaan");
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetailSeries();
  }, [detailHabit, progressData, isEnglish]);

  useEffect(() => {
    const computeDetailTicks = () => {
      if (!detailSeries || detailSeries.length === 0) {
        setDetailBottomTicks([]);
        setDetailTopTicks([]);
        return;
      }
      const uniqueDates = Array.from(new Set(detailSeries.map((s) => s.date)));
      const tt = [];
      const monthSeen = new Set();
      uniqueDates.forEach((dateStr) => {
        const d = new Date(dateStr);
        const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
        if (!monthSeen.has(monthKey)) {
          monthSeen.add(monthKey);
          tt.push(dateStr);
        }
      });
      const bt = uniqueDates;
      setDetailBottomTicks(bt);
      setDetailTopTicks(tt);
    };
    computeDetailTicks();
  }, [detailSeries, detailShowAllDates]);

  const formatMonthYearTop = (dateStr) => {
    const d = new Date(dateStr);
    return isEnglish
      ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
      : d.toLocaleDateString("id-ID", { month: "short", year: "numeric" });
  };

  const CustomDotLabelModal = (props) => {
    const { x, y, payload } = props;
    if (payload === undefined) return null;
    const date = new Date(payload.date);
    const day = date.getDate();
    const value = payload.value;

    // Position label below the dot
    const labelY = y + 20;

    return (
      <g>
        <text
          x={x}
          y={labelY}
          textAnchor="middle"
          fontSize={11}
          fill={value === 1 ? "#16a34a" : "#dc2626"}
          fontWeight={500}
        >
          {day}
        </text>
      </g>
    );
  };

  if (loading && !habitsData) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="habits-loading">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full sm:w-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-12 w-full rounded-xl" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-xl" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-16 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (initializing) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-muted-foreground">{isEnglish ? "Preparing default habits..." : "Menyiapkan kebiasaan default..."}</p>
        <p className="text-sm text-muted-foreground">{isEnglish ? "19 habits ready to use" : "19 kebiasaan siap digunakan"}</p>
      </div>
    );
  }

  // Render Today View
  if (activeTab === "today") {
    return (
      <>
      <div className="max-w-4xl mx-auto px-4 py-4 md:px-6 md:py-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">{isEnglish ? "Daily Habits" : "Kebiasaan Harian"}</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {isEnglish
                  ? `${habitsData?.total_completed || 0} of ${habitsData?.total_habits || 0} habits completed today`
                  : `${habitsData?.total_completed || 0} dari ${habitsData?.total_habits || 0} habits selesai hari ini`}
              </p>
            </div>
            <button
              onClick={() => loadData()}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              title={isEnglish ? "Refresh" : "Refresh"}
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isEnglish ? "Last refreshed" : "Terakhir di-refresh"}: {lastRefresh.toLocaleTimeString(isEnglish ? 'en-US' : 'id-ID', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* Period Tabs */}
        <div className="grid grid-cols-2 sm:flex gap-1.5 md:gap-2 mb-3 md:mb-4">
          <Button
            variant={activeTab === "today" ? "default" : "outline"}
            onClick={() => setActiveTab("today")}
            className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
          >
            <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">{isEnglish ? "Today" : "Hari Ini"}</span>
            <span className="sm:hidden">{isEnglish ? "Today" : "Hari Ini"}</span>
          </Button>
          <Button
            variant={activeTab === "daily" ? "default" : "outline"}
            onClick={() => setActiveTab("daily")}
            className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
          >
            <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" />
            {isEnglish ? "Daily" : "Harian"}
          </Button>
          <Button
            variant={activeTab === "weekly" ? "default" : "outline"}
            onClick={() => setActiveTab("weekly")}
            className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
          >
            {isEnglish ? "Weekly" : "Mingguan"}
          </Button>
          <Button
            variant={activeTab === "monthly" ? "default" : "outline"}
            onClick={() => setActiveTab("monthly")}
            className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
          >
            {isEnglish ? "Monthly" : "Bulanan"}
          </Button>
          <Button
            variant={activeTab === "yearly" ? "default" : "outline"}
            onClick={() => setActiveTab("yearly")}
            className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
          >
            {isEnglish ? "Yearly" : "Tahunan"}
          </Button>
        </div>

        {/* Add Custom Habit Button - Premium Floating Action Button */}
        <div className="fixed bottom-20 md:bottom-24 right-4 md:right-6 z-40">
          <Dialog open={showAddHabit} onOpenChange={setShowAddHabit}>
            <DialogTrigger asChild>
              <Button
                size="icon"
                className="h-12 w-12 md:h-14 md:w-14 rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 bg-gradient-to-r from-primary to-primary/80"
                title={isEnglish ? "Add New Habit" : "Tambah Kebiasaan Baru"}
              >
                <Plus className="w-6 h-6" />
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{isEnglish ? "Add New Habit" : "Tambah Kebiasaan Baru"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="habit-name">{isEnglish ? "Habit Name *" : "Nama Kebiasaan *"}</Label>
                <Input
                  id="habit-name"
                  placeholder={isEnglish ? "Example: Monday fasting" : "Contoh: Puasa Senin"}
                  value={newHabit.name}
                  onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="habit-desc">{isEnglish ? "Description" : "Deskripsi"}</Label>
                <Input
                  id="habit-desc"
                  placeholder={isEnglish ? "Short description..." : "Deskripsi singkat..."}
                  value={newHabit.description}
                  onChange={(e) => setNewHabit({ ...newHabit, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="habit-category">{isEnglish ? "Category" : "Kategori"}</Label>
                <Select value={newHabit.category} onValueChange={(value) => setNewHabit({ ...newHabit, category: value })}>
                  <SelectTrigger id="habit-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quran">Al-Qur'an</SelectItem>
                    <SelectItem value="shalat">{isEnglish ? "Prayer" : "Shalat"}</SelectItem>
                    <SelectItem value="infaq">Infaq</SelectItem>
                    <SelectItem value="intentions">{isEnglish ? "Intentions" : "Niat & Doa"}</SelectItem>
                    <SelectItem value="general">{isEnglish ? "Other" : "Lainnya"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="habit-target">{isEnglish ? "Daily Target" : "Target per Hari"}</Label>
                <Input
                  id="habit-target"
                  type="number"
                  min="1"
                  value={newHabit.target_per_day}
                  onChange={(e) => setNewHabit({ ...newHabit, target_per_day: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddHabit(false)}>
                {isEnglish ? "Cancel" : "Batal"}
              </Button>
              <Button onClick={addCustomHabit} disabled={!newHabit.name.trim()}>
                {isEnglish ? "Add" : "Tambahkan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>

        {/* Progress Overview - Premium */}
        <Card className="mb-4 md:mb-6 overflow-hidden">
          <div className="islamic-gradient text-white">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg md:text-xl font-bold">{isEnglish ? "Today's Progress" : "Progress Hari Ini"}</h3>
                    {habitsData?.date && (
                      <span className="text-xs md:text-sm text-white/90 font-medium">
                        {isEnglish
                          ? new Date(habitsData.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                          : new Date(habitsData.date + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  <p className="text-white/80 text-xs md:text-sm">
                    {isEnglish
                      ? `${habitsData?.total_completed || 0} of ${habitsData?.total_habits || 0} completed`
                      : `${habitsData?.total_completed || 0} dari ${habitsData?.total_habits || 0} selesai`}
                  </p>
                </div>
                <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Award className="w-7 h-7 md:w-10 md:h-10" />
                </div>
              </div>

              {/* Progress Stats */}
              <div className="grid grid-cols-3 gap-2 md:gap-4 mb-3 md:mb-4">
                <div className="text-center p-2 md:p-3 bg-white/10 rounded-lg md:rounded-xl backdrop-blur-sm">
                  <p className="text-xl md:text-2xl font-bold">{habitsData?.total_completed || 0}</p>
                  <p className="text-[10px] md:text-xs text-white/80">{isEnglish ? "Completed" : "Selesai"}</p>
                </div>
                <div className="text-center p-2 md:p-3 bg-white/10 rounded-lg md:rounded-xl backdrop-blur-sm">
                  <p className="text-xl md:text-2xl font-bold">{habitsData?.total_habits || 0}</p>
                  <p className="text-[10px] md:text-xs text-white/80">{isEnglish ? "Total" : "Total"}</p>
                </div>
                <div className="text-center p-2 md:p-3 bg-white/10 rounded-lg md:rounded-xl backdrop-blur-sm">
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center ${
                        (habitsData?.streak_days || 0) > 0
                          ? "bg-gradient-to-br from-orange-400 to-red-500"
                          : "bg-white/20"
                      } ${(habitsData?.streak_days || 0) >= 7 ? "animate-pulse" : ""}`}
                    >
                      <Flame className="w-4 h-4 md:w-5 md:h-5 text-white" />
                    </div>
                    <p className="text-xl md:text-2xl font-bold">{habitsData?.streak_days || 0}</p>
                  </div>
                  <p className="text-[10px] md:text-xs text-white/80">{isEnglish ? "Day Streak" : "Streak Hari"}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-1.5 md:space-y-2">
                <div className="flex justify-between text-xs md:text-sm">
                  <span className="font-medium">{isEnglish ? "Completion Rate" : "Tingkat Penyelesaian"}</span>
                  <span className="font-bold">{Math.round(((habitsData?.total_completed || 0) / (habitsData?.total_habits || 1)) * 100)}%</span>
                </div>
                <div className="bg-white/20 rounded-full h-2 md:h-3 overflow-hidden backdrop-blur-sm">
                  <div
                    className="bg-white rounded-full h-2 md:h-3 transition-all duration-500 shadow-lg"
                    style={{ width: `${Math.round(((habitsData?.total_completed || 0) / (habitsData?.total_habits || 1)) * 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </div>
        </Card>

        {/* Category Distribution (Today) */}
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-base md:text-lg font-semibold">{isEnglish ? "Category Distribution" : "Distribusi Kategori"}</h3>
              <Badge variant="outline" className="text-xs md:text-sm">
                {getTodayCategoryChartData().reduce((sum, item) => sum + item.value, 0)} {isEnglish ? "completed" : "selesai"}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 items-center">
              <div className="h-48 md:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getTodayCategoryChartData()}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={40}
                      outerRadius={75}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      fontSize={11}
                      fontWeight={600}
                    >
                      {getTodayCategoryChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => {
                        const list = getTodayCategoryChartData();
                        const total = list.reduce((sum, item) => sum + item.value, 0) || 1;
                        const pct = Math.round((value / total) * 100);
                        return [`${value} (${pct}%)`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center gap-1.5 md:gap-2">
                {getTodayCategoryChartData().length === 0 ? (
                  <p className="text-xs md:text-sm text-muted-foreground">{isEnglish ? "No completed habits yet" : "Belum ada kebiasaan selesai"}</p>
                ) : (
                  (() => {
                    const list = getTodayCategoryChartData();
                    const total = list.reduce((sum, item) => sum + item.value, 0) || 1;
                    return list.map((d) => {
                      const pct = Math.round((d.value / total) * 100);
                      return (
                        <div key={d.key} className="flex items-center gap-1.5 md:gap-2">
                          <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }}></span>
                          <span className="text-xs md:text-sm truncate flex-1 min-w-0">{d.name}</span>
                          <span className="text-xs md:text-sm font-bold flex-shrink-0">{pct}%</span>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Habits by Category - Premium */}
        {habitsData?.categories && Object.entries(habitsData.categories).map(([key, category]) => {
          if (category.habits.length === 0) return null;

          return (
            <div key={key} className="mb-4 md:mb-6">
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4 px-2">
                <div className="w-1 h-6 md:h-8 rounded-full" style={{ backgroundColor: categoryColors[key] || "#6B7280" }}></div>
                <h2 className="text-lg md:text-xl font-bold">{category.name}</h2>
                <span className="text-xs md:text-sm text-muted-foreground ml-auto">{category.habits.length} habits</span>
              </div>

              <div className="space-y-2 md:space-y-3">
                {category.habits.map((habit) => {
                  const IconComponent = iconMap[habit.icon] || BookOpen;
                  const isCompleted = habit.completed;
                  const isSystemDefault = habit.is_system_default;

                  return (
                    <Card
                      key={habit.habit_id}
                      className={`group hover:shadow-lg md:hover:shadow-xl transition-all duration-300 ${
                        isCompleted ? "border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20" : ""
                      } ${habit.enabled ? "" : "opacity-50"} ${isSystemDefault ? "border-l-4 border-l-blue-500" : ""}`}
                    >
                      <CardContent className="p-3 md:p-5">
                        <div className="flex items-center gap-2 md:gap-4">
                          {/* Premium Checkbox */}
                          <div
                            className={`w-7 h-7 md:w-8 md:h-8 rounded-lg md:rounded-xl border-2 flex items-center justify-center transition-all cursor-pointer shadow-sm flex-shrink-0 ${
                              isCompleted
                                ? "bg-green-500 border-green-500 text-white shadow-lg shadow-green-500/30 scale-105 md:scale-110"
                                : "border-gray-300 hover:border-primary hover:scale-105 md:hover:scale-110"
                            }`}
                            onClick={() => {
                              if (habit.enabled) {
                                toggleHabit(habit.habit_id, isCompleted);
                              }
                            }}
                          >
                            {isCompleted ? <Check className="w-4 h-4 md:w-5 md:h-5" /> : <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full border-2 border-current opacity-30 group-hover:opacity-100" />}
                          </div>

                          {/* Icon */}
                          <div
                            className="w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center relative shadow-md group-hover:shadow-lg transition-all flex-shrink-0"
                            style={{ backgroundColor: `${habit.color}20` }}
                          >
                            <IconComponent className="w-5 h-5 md:w-7 md:h-7" style={{ color: habit.color }} />
                            {isSystemDefault && (
                              <div className="absolute -top-1 md:-top-1.5 -right-1 md:-right-1.5 w-4 h-4 md:w-5 md:h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-md">
                                <Star className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-white" />
                              </div>
                            )}
                            {isCompleted && (
                              <div className="absolute -bottom-0.5 md:-bottom-1 -right-1 md:-right-1.5 w-4 h-4 md:w-5 md:h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                                <CheckCircle2 className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => {
                              if (habit.enabled) {
                                toggleHabit(habit.habit_id, isCompleted);
                              }
                            }}
                          >
                            <h3 className={`font-bold text-sm md:text-base mb-0.5 md:mb-1 ${isCompleted ? "line-through text-muted-foreground" : ""}`}>
                              {habit.name}
                            </h3>
                            {habit.description && (
                              <p className="text-xs md:text-sm text-muted-foreground truncate">{habit.description}</p>
                            )}
                            {habit.target_per_day > 1 && (
                              <div className="mt-1.5 md:mt-2 flex items-center gap-1.5 md:gap-2">
                                <div className="flex-1 bg-muted rounded-full h-1.5 md:h-2 overflow-hidden">
                                  <div
                                    className="h-full bg-primary transition-all duration-500"
                                    style={{ width: `${Math.min((habit.completed_count || 0) / habit.target_per_day * 100, 100)}%` }}
                                  />
                                </div>
                                <span className="text-[10px] md:text-xs font-medium text-muted-foreground flex-shrink-0">
                                  {habit.completed_count || 0}/{habit.target_per_day}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 md:gap-1">
                            {/* View Chart */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailHabit(habit);
                              }}
                              className="p-1.5 md:p-2.5 rounded-lg md:rounded-xl hover:bg-primary/10 hover:text-primary transition-all hidden sm:block"
                              title={isEnglish ? "View Chart" : "Lihat Grafik"}
                            >
                              <TrendingUp className="w-4 h-4 md:w-5 md:h-5" />
                            </button>
                            {/* Enable/Disable Toggle */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleHabitEnabled(habit.habit_id, habit.enabled);
                              }}
                              className="p-1.5 md:p-2.5 rounded-lg md:rounded-xl hover:bg-primary/10 hover:text-primary transition-all"
                              title={habit.enabled ? (isEnglish ? "Disable" : "Nonaktifkan") : (isEnglish ? "Enable" : "Aktifkan")}
                            >
                              {habit.enabled ? (
                                <ToggleRight className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                              )}
                            </button>

                            {/* Delete Button (shows modal for all; system defaults cannot be deleted) */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDelete(habit);
                              }}
                              className={`p-1 md:p-1.5 rounded-lg transition-colors ${
                                isSystemDefault
                                  ? "hover:bg-orange-100 text-orange-600"
                                  : "hover:bg-destructive/10 text-destructive"
                              }`}
                              title={isSystemDefault ? (isEnglish ? "Disable Habit" : "Nonaktifkan Kebiasaan") : (isEnglish ? "Delete" : "Hapus")}
                            >
                              <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                            </button>

                            {/* Target badge */}
                            {habit.target_per_day > 1 && (
                              <div className="text-[10px] md:text-xs bg-muted px-1.5 md:px-2 py-0.5 md:py-1 rounded-full hidden xs:block">
                                {isEnglish ? `${habit.target_per_day}x/day` : `${habit.target_per_day}x/hari`}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Habit Detail Chart Modal */}
      <Dialog open={!!detailHabit} onOpenChange={(open) => { if (!open) { setDetailHabit(null); setDetailSeries([]); setDetailShowAllDates(false); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEnglish ? "Habit Detail" : "Detail Kebiasaan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {detailHabit && (
                <>
                  {(iconMap[detailHabit.icon] || BookOpen) && React.createElement(iconMap[detailHabit.icon] || BookOpen, { className: "w-5 h-5", style: { color: detailHabit.color } })}
                  <span className="font-medium">{detailHabit.name}</span>
                </>
              )}
            </div>
            <div className="flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDetailShowAllDates(!detailShowAllDates)}
                className="text-xs"
              >
                {detailShowAllDates
                  ? (isEnglish ? "Show Weekly" : "Tampilkan Mingguan")
                  : (isEnglish ? "Show All Dates" : "Tampilkan Semua Tanggal")}
              </Button>
            </div>
            <div className="h-64">
              {detailLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : detailSeries.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground text-sm">{isEnglish ? "No data available" : "Tidak ada data"}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={detailSeries} margin={{ top: 50, right: 10, left: -20, bottom: 40 }} className="habit-detail-chart">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      ticks={detailBottomTicks}
                      allowDuplicatedCategory={false}
                      tickFormatter={(v) => new Date(v).getDate()}
                      interval={0}
                      tick={{ fontSize: detailShowAllDates ? 9 : 10, fill: "#6b7280" }}
                      tickMargin={8}
                      dy={10}
                    />
                    <XAxis
                      dataKey="date"
                      ticks={detailTopTicks}
                      allowDuplicatedCategory={false}
                      tickFormatter={formatMonthYearTop}
                      orientation="top"
                      axisLine={false}
                      tickLine={false}
                      dy={-5}
                      fontSize={11}
                      fontWeight={500}
                    />
                    <YAxis
                      domain={[0, 1]}
                      ticks={[0, 1]}
                      tickFormatter={(v) => (v === 1 ? (isEnglish ? "Yes" : "Ya") : (isEnglish ? "No" : "Tidak"))}
                    />
                    <RechartsTooltip
                      formatter={(value) => [(value === 1 ? (isEnglish ? "Yes" : "Ya") : (isEnglish ? "No" : "Tidak")), isEnglish ? "Done" : "Dilakukan"]}
                      labelFormatter={(label) => {
                        const d = new Date(label);
                        return isEnglish
                          ? d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
                          : d.toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={detailHabit?.color || "#0ea5e9"}
                      strokeWidth={2}
                      dot={{ r: detailShowAllDates ? 3 : 4 }}
                      activeDot={{ r: 6 }}
                      label={<CustomDotLabelModal />}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailHabit(null)}>{isEnglish ? "Close" : "Tutup"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Modal */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) { setConfirmDelete(null); setConfirmAgree(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDelete?.is_system_default
                ? (isEnglish ? "Disable Default Habit" : "Nonaktifkan Kebiasaan Default")
                : (isEnglish ? "Delete Custom Habit" : "Hapus Kebiasaan Kustom")
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold">{confirmDelete?.name}</p>
                {confirmDelete?.is_system_default && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {isEnglish ? "Default" : "Bawaan"}
                  </span>
                )}
              </div>
              {confirmDelete?.description && (
                <p className="text-sm text-muted-foreground">{confirmDelete.description}</p>
              )}
              {typeof confirmDelete?.target_per_day === "number" && confirmDelete?.target_per_day > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {isEnglish ? "Target per day" : "Target per hari"}: {confirmDelete.target_per_day}
                </p>
              )}
            </div>

            {confirmDelete?.is_system_default ? (
              <>
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {isEnglish
                      ? "⭐ This is a default habit that cannot be deleted. You can only disable it. You can re-enable it later from Settings."
                      : "⭐ Ini adalah kebiasaan default yang tidak bisa dihapus. Anda hanya bisa menonaktifkannya. Anda bisa mengaktifkannya lagi nanti melalui Pengaturan."}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {confirmDelete?.enabled
                    ? (isEnglish
                        ? "This habit will be disabled and hidden from your daily list. You can re-enable it later."
                        : "Kebiasaan ini akan dinonaktifkan dan disembunyikan dari daftar harian Anda. Anda bisa mengaktifkannya lagi nanti.")
                    : (isEnglish
                        ? "This habit is currently disabled. Enable it to add it back to your daily list."
                        : "Kebiasaan ini sedang dinonaktifkan. Aktifkan untuk menambahkannya kembali ke daftar harian Anda.")
                  }
                </p>
              </>
            ) : (
              <>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 p-3">
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    {isEnglish
                      ? "🗑️ This is your custom habit. Deleting it will permanently remove it and all its history."
                      : "🗑️ Ini adalah kebiasaan kustom Anda. Menghapusnya akan membuangnya permanen beserta semua riwayatnya."}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? "This action is permanent and cannot be undone." : "Tindakan ini permanen dan tidak bisa dibatalkan."}
                </p>
              </>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmAgree}
                onChange={(e) => setConfirmAgree(e.target.checked)}
                className="h-4 w-4 rounded border"
              />
              {confirmDelete?.is_system_default
                ? (confirmDelete?.enabled
                    ? (isEnglish
                        ? "I understand and want to disable this default habit"
                        : "Saya paham dan ingin menonaktifkan kebiasaan default ini")
                    : (isEnglish
                        ? "I understand and want to enable this default habit"
                        : "Saya paham dan ingin mengaktifkan kebiasaan default ini"))
                : (isEnglish
                    ? "I understand and want to permanently delete this custom habit"
                    : "Saya paham dan ingin menghapus permanen kebiasaan kustom ini")
                }
              </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            {confirmDelete?.is_system_default ? (
              <Button
                variant="default"
                disabled={!confirmAgree}
                onClick={async () => {
                  if (confirmDelete?.habit_id) {
                    await toggleHabitEnabled(confirmDelete.habit_id, confirmDelete.enabled);
                  }
                  setConfirmDelete(null);
                  setConfirmAgree(false);
                }}
              >
                {confirmDelete?.enabled
                  ? (isEnglish ? "Disable Habit" : "Nonaktifkan")
                  : (isEnglish ? "Enable Habit" : "Aktifkan")
                }
              </Button>
            ) : (
              <Button
                variant="destructive"
                disabled={!confirmAgree}
                onClick={async () => {
                  if (confirmDelete?.habit_id) {
                    await deleteHabit(confirmDelete.habit_id);
                  }
                  setConfirmDelete(null);
                  setConfirmAgree(false);
                }}
              >
                {isEnglish ? "Delete Permanently" : "Hapus Permanen"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  return (
    <>
    <div className="max-w-4xl mx-auto px-4 py-4 md:px-6 md:py-6">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">{isEnglish ? "Habit Progress" : "Progress Kebiasaan"}</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          {isEnglish ? "See your activity progress" : "Lihat progress amalan Anda"}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:flex gap-1.5 md:gap-2 mb-3 md:mb-4">
        <Button
          variant={activeTab === "today" ? "default" : "outline"}
          onClick={() => setActiveTab("today")}
          className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
        >
          <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" />
          {isEnglish ? "Today" : "Hari Ini"}
        </Button>
        <Button
          variant={activeTab === "daily" ? "default" : "outline"}
          onClick={() => setActiveTab("daily")}
          className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
        >
          <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1 md:mr-2" />
          {isEnglish ? "Daily" : "Harian"}
        </Button>
        <Button
          variant={activeTab === "weekly" ? "default" : "outline"}
          onClick={() => setActiveTab("weekly")}
          className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
        >
          {isEnglish ? "Weekly" : "Mingguan"}
        </Button>
        <Button
          variant={activeTab === "monthly" ? "default" : "outline"}
          onClick={() => setActiveTab("monthly")}
          className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
        >
          {isEnglish ? "Monthly" : "Bulanan"}
        </Button>
        <Button
          variant={activeTab === "yearly" ? "default" : "outline"}
          onClick={() => setActiveTab("yearly")}
          className="w-full sm:w-auto whitespace-nowrap text-xs md:text-sm h-9 md:h-10"
        >
          {isEnglish ? "Yearly" : "Tahunan"}
        </Button>
      </div>

      {/* Quick Links to Settings and Analytics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3 mb-4 md:mb-6">
        <Link to="/settings">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-sm md:text-base">{isEnglish ? "Reminders & Settings" : "Pengingat & Pengaturan"}</h3>
                  <p className="text-[10px] md:text-xs text-muted-foreground truncate">{isEnglish ? "Set reminders and customize app settings" : "Atur pengingat dan kustomisasi pengaturan"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/analytics">
          <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-sm md:text-base">{isEnglish ? "Analytics" : "Analitik"}</h3>
                  <p className="text-[10px] md:text-xs text-muted-foreground truncate">{isEnglish ? "View detailed progress analytics" : "Lihat analitik progress detail"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {activeTab !== "today" && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 md:gap-3 items-end">
              <div className="space-y-1">
                <Label htmlFor="range-start" className="text-xs md:text-sm">{isEnglish ? "Start" : "Mulai"}</Label>
                <Input
                  id="range-start"
                  type="date"
                  value={dateRange.start}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, start: event.target.value }))}
                  className="h-9 md:h-10 text-xs md:text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="range-end" className="text-xs md:text-sm">{isEnglish ? "End" : "Sampai"}</Label>
                <Input
                  id="range-end"
                  type="date"
                  value={dateRange.end}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, end: event.target.value }))}
                  className="h-9 md:h-10 text-xs md:text-sm"
                />
              </div>
              <Button
                variant="outline"
                className="w-full sm:w-auto text-xs md:text-sm h-9 md:h-10"
                onClick={() => setDateRange({ start: "", end: "" })}
              >
                {isEnglish ? "Reset" : "Reset"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : progressData && (
        <>
          <Card className="mb-4 md:mb-6 bg-gradient-to-r from-islamic-teal to-islamic-navy text-white">
            <CardContent className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4">
                {isEnglish
                  ? `Summary ${progressData.period === "custom" ? "Date Range" : activeTab === "daily" ? "Daily" : activeTab === "weekly" ? "Weekly" : activeTab === "monthly" ? "Monthly" : "Yearly"}`
                  : `Ringkasan ${progressData.period === "custom" ? "Rentang Tanggal" : activeTab === "daily" ? "Harian" : activeTab === "weekly" ? "Mingguan" : activeTab === "monthly" ? "Bulanan" : "Tahunan"}`}
              </h2>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <p className="text-white/80 text-xs md:text-sm">{isEnglish ? "Period" : "Periode"}</p>
                  <p className="text-xl md:text-2xl font-bold">
                    {isEnglish
                      ? progressData.period === "custom" ? "Range" : progressData.period === "daily" ? "Daily" : progressData.period === "weekly" ? "Weekly" : progressData.period === "monthly" ? "Monthly" : "Yearly"
                      : progressData.period === "custom" ? "Rentang" : progressData.period === "daily" ? "Harian" : progressData.period === "weekly" ? "Mingguan" : progressData.period === "monthly" ? "Bulanan" : "Tahunan"}
                  </p>
                  <p className="text-white/70 text-[10px] md:text-xs">{isEnglish ? `${progressData.days} days` : `${progressData.days} hari`}</p>
                </div>
                <div>
                  <p className="text-white/80 text-xs md:text-sm">Total Logs</p>
                  <p className="text-xl md:text-2xl font-bold">{progressData.total_logs}</p>
                </div>
              </div>
              <div className="col-span-2 mt-3 md:mt-4">
                <p className="text-white/70 text-[10px] md:text-xs mb-1.5 md:mb-2">
                  {isEnglish ? `From ${progressData.start_date} to ${progressData.end_date}` : `Dari ${progressData.start_date} s.d. ${progressData.end_date}`}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Category Distribution (Selected Period) */}
          <Card className="mb-4 md:mb-6">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-semibold">{isEnglish ? "Category Distribution" : "Distribusi Kategori"}</h3>
                <Badge variant="outline" className="text-xs md:text-sm">
                  {getProgressCategoryChartData().reduce((sum, item) => sum + item.value, 0)} {isEnglish ? "total" : "total"}
                </Badge>
              </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 items-center">
              <div className="h-48 md:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getProgressCategoryChartData()}
                        dataKey="value"
                        nameKey="name"
                      innerRadius={40}
                      outerRadius={75}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      fontSize={11}
                      fontWeight={600}
                      >
                        {getProgressCategoryChartData().map((entry, index) => (
                          <Cell key={`cell-period-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => {
                          const list = getProgressCategoryChartData();
                          const total = list.reduce((sum, item) => sum + item.value, 0) || 1;
                          const pct = Math.round((value / total) * 100);
                          return [`${value} (${pct}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              <div className="flex flex-col justify-center gap-1.5 md:gap-2">
                {getProgressCategoryChartData().length === 0 ? (
                    <p className="text-xs md:text-sm text-muted-foreground">{isEnglish ? "No data in this period" : "Tidak ada data pada periode ini"}</p>
                  ) : (
                  (() => {
                    const list = getProgressCategoryChartData();
                    const total = list.reduce((sum, item) => sum + item.value, 0) || 1;
                    return list.map((d) => {
                      const pct = Math.round((d.value / total) * 100);
                      return (
                        <div key={d.key} className="flex items-center gap-1.5 md:gap-2">
                          <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }}></span>
                          <span className="text-xs md:text-sm truncate flex-1 min-w-0">{d.name}</span>
                          <span className="text-xs md:text-sm font-bold flex-shrink-0">{pct}%</span>
                        </div>
                      );
                    });
                  })()
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Chart */}
          {activeTab !== "today" && progressData?.daily_stats && (
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {isEnglish ? "Progress Trend" : "Tren Progress"}
                  </h3>
                  <div className="flex items-center gap-2 text-sm">
                    {(() => {
                      const stats = progressData.daily_stats;
                      const recent = stats.slice(-7);
                      const avgCompletion = recent.reduce((sum, s) => sum + (s.completion_rate || 0), 0) / recent.length;
                      const earlier = stats.slice(0, -7).length > 0
                        ? stats.slice(0, -7)
                        : stats.slice(0, Math.min(7, stats.length - 1));
                      const earlierAvg = earlier.reduce((sum, s) => sum + (s.completion_rate || 0), 0) / earlier.length;
                      const isImproving = avgCompletion >= earlierAvg;

                      return (
                        <>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                            isImproving
                              ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                          }`}>
                            {isImproving ? (
                              <>
                                <TrendingUp className="w-4 h-4" />
                                {isEnglish ? "Improving" : "Meningkat"}
                              </>
                            ) : (
                              <>
                                <TrendingUp className="w-4 h-4 transform rotate-180" />
                                {isEnglish ? "Declining" : "Menurun"}
                              </>
                            )}
                          </span>
                          <span className="text-muted-foreground">
                            {avgCompletion.toFixed(1)}% avg
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="space-y-3">
                  {progressData.daily_stats.slice(-14).map((day, index) => {
                    const completionRate = day.completion_rate || 0;
                    const isToday = index === progressData.daily_stats.slice(-14).length - 1;
                    const date = new Date(day.date);
                    const dayName = isEnglish
                      ? date.toLocaleDateString('en-US', { weekday: 'short' })
                      : date.toLocaleDateString('id-ID', { weekday: 'short' });
                    const dayDate = date.getDate();

                    return (
                      <div key={day.date} className="flex items-center gap-3">
                        <div className="w-16 text-xs text-muted-foreground text-right">
                          <div>{dayName}</div>
                          <div className="font-medium">{dayDate}</div>
                        </div>
                        <div className="flex-1 h-8 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              completionRate >= 80
                                ? "bg-gradient-to-r from-green-500 to-green-400"
                                : completionRate >= 50
                                ? "bg-gradient-to-r from-yellow-500 to-yellow-400"
                                : "bg-gradient-to-r from-red-500 to-red-400"
                            } ${isToday ? "opacity-80" : ""}`}
                            style={{ width: `${completionRate}%` }}
                          />
                        </div>
                        <div className="w-12 text-sm font-semibold text-right">
                          {completionRate.toFixed(0)}%
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-500 to-green-400"></div>
                    <span>{isEnglish ? "Excellent (≥80%)" : "Bagus (≥80%)"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-400"></div>
                    <span>{isEnglish ? "Good (50-79%)" : "Cukup (50-79%)"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-red-400"></div>
                    <span>{isEnglish ? "Need Improvement (<50%)" : "Perlu Perbaikan (<50%)"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {["quran", "shalat", "infaq"].map((category) => {
            const categoryHabits = progressData.habits.filter(h => h.category === category);

            if (categoryHabits.length === 0) return null;

            return (
              <Card key={category} className="mb-4">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: categoryColors[category] }}>
                    {category === "quran" ? "Al-Qur'an" : category === "shalat" ? (isEnglish ? "Prayer" : "Shalat") : "Infaq"}
                  </h3>
                  <div className="space-y-4">
                    {categoryHabits.map((habit) => {
                      const IconComponent = iconMap[habit.icon] || BookOpen;

                      return (
                        <div key={habit.habit_id}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-5 h-5" style={{ color: habit.color }} />
                              <span className="font-medium">{habit.name}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {habit.total_completed} / {habit.target}
                            </span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2">
                            <div
                              className="rounded-full h-2 transition-all"
                              style={{
                                width: `${habit.completion_rate}%`,
                                backgroundColor: habit.color,
                              }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 text-right">
                            {isEnglish ? `${habit.completion_rate}% completed` : `${habit.completion_rate}% selesai`}
                          </p>
                          <div className="mt-2 flex justify-end">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/habits/${habit.habit_id}`)}>
                              <TrendingUp className="w-4 h-4 mr-1" /> {isEnglish ? "View Chart" : "Lihat Grafik"}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
      </div>

      {/* Confirm Delete Modal */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) { setConfirmDelete(null); setConfirmAgree(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDelete?.is_system_default
                ? (isEnglish ? "Disable Default Habit" : "Nonaktifkan Kebiasaan Default")
                : (isEnglish ? "Delete Custom Habit" : "Hapus Kebiasaan Kustom")
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted p-3">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-semibold">{confirmDelete?.name}</p>
                {confirmDelete?.is_system_default && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                    {isEnglish ? "Default" : "Bawaan"}
                  </span>
                )}
              </div>
              {confirmDelete?.description && (
                <p className="text-sm text-muted-foreground">{confirmDelete.description}</p>
              )}
              {typeof confirmDelete?.target_per_day === "number" && confirmDelete?.target_per_day > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {isEnglish ? "Target per day" : "Target per hari"}: {confirmDelete.target_per_day}
                </p>
              )}
            </div>

            {confirmDelete?.is_system_default ? (
              <>
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {isEnglish
                      ? "⭐ This is a default habit that cannot be deleted. You can only disable it. You can re-enable it later from Settings."
                      : "⭐ Ini adalah kebiasaan default yang tidak bisa dihapus. Anda hanya bisa menonaktifkannya. Anda bisa mengaktifkannya lagi nanti melalui Pengaturan."}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {confirmDelete?.enabled
                    ? (isEnglish
                        ? "This habit will be disabled and hidden from your daily list. You can re-enable it later."
                        : "Kebiasaan ini akan dinonaktifkan dan disembunyikan dari daftar harian Anda. Anda bisa mengaktifkannya lagi nanti.")
                    : (isEnglish
                        ? "This habit is currently disabled. Enable it to add it back to your daily list."
                        : "Kebiasaan ini sedang dinonaktifkan. Aktifkan untuk menambahkannya kembali ke daftar harian Anda.")
                  }
                </p>
              </>
            ) : (
              <>
                <div className="rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 p-3">
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    {isEnglish
                      ? "🗑️ This is your custom habit. Deleting it will permanently remove it and all its history."
                      : "🗑️ Ini adalah kebiasaan kustom Anda. Menghapusnya akan membuangnya permanen beserta semua riwayatnya."}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isEnglish ? "This action is permanent and cannot be undone." : "Tindakan ini permanen dan tidak bisa dibatalkan."}
                </p>
              </>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={confirmAgree}
                onChange={(e) => setConfirmAgree(e.target.checked)}
                className="h-4 w-4 rounded border"
              />
              {confirmDelete?.is_system_default
                ? (confirmDelete?.enabled
                    ? (isEnglish
                        ? "I understand and want to disable this default habit"
                        : "Saya paham dan ingin menonaktifkan kebiasaan default ini")
                    : (isEnglish
                        ? "I understand and want to enable this default habit"
                        : "Saya paham dan ingin mengaktifkan kebiasaan default ini"))
                : (isEnglish
                    ? "I understand and want to permanently delete this custom habit"
                    : "Saya paham dan ingin menghapus permanen kebiasaan kustom ini")
                }
              </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {isEnglish ? "Cancel" : "Batal"}
            </Button>
            {confirmDelete?.is_system_default ? (
              <Button
                variant="default"
                disabled={!confirmAgree}
                onClick={async () => {
                  if (confirmDelete?.habit_id) {
                    await toggleHabitEnabled(confirmDelete.habit_id, confirmDelete.enabled);
                  }
                  setConfirmDelete(null);
                  setConfirmAgree(false);
                }}
              >
                {confirmDelete?.enabled
                  ? (isEnglish ? "Disable Habit" : "Nonaktifkan")
                  : (isEnglish ? "Enable Habit" : "Aktifkan")
                }
              </Button>
            ) : (
              <Button
                variant="destructive"
                disabled={!confirmAgree}
                onClick={async () => {
                  if (confirmDelete?.habit_id) {
                    await deleteHabit(confirmDelete.habit_id);
                  }
                  setConfirmDelete(null);
                  setConfirmAgree(false);
                }}
              >
                {isEnglish ? "Delete Permanently" : "Hapus Permanen"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
};

export default Habits;
